import { useState, useRef, useCallback } from 'react';

export const useOtpInput = (length: number = 4) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const otpValue = otp.join('');

  const handleInputChange = useCallback((value: string, index: number) => {
    const char = value.replace(/\D/g, "").slice(-1); // Only take the last digit character
    const newOtp = [...otp];
    newOtp[index] = char;
    setOtp(newOtp);

    // Move to next input if value is entered
    if (char && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp, length]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp, length]);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;

    const newOtp = [...otp];
    pasted.split("").forEach((char, i) => {
      newOtp[i] = char;
    });
    setOtp(newOtp);

    const nextIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  }, [otp, length]);

  const handleFocus = useCallback((index: number) => {
    inputRefs.current[index]?.select();
  }, []);

  const clear = useCallback(() => {
    setOtp(new Array(length).fill(''));
    inputRefs.current[0]?.focus();
  }, [length]);

  return {
    otp,
    inputRefs,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
    otpValue,
    clear,
  };
};
